import { DEFAULT_LICENSE_PRODUCT } from '../../../actions/constants/LicenseProductConstant';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import { CompanyAction } from '../../../actions/CompanyAction';
import { SmartDentConstant } from '../../../actions/constants/SmartDentConstant';
import { isLicenseCorpCompany, isRootCompany } from '../../../lib/licenseHelper';
import {
    collectDescendantCompanyIds,
    collectHqDirectCompanyIds,
    resolveLicenseOwnerId,
} from '../../../lib/issuanceScope';
import { applyCustomerLicenseFilters, filterIssueHistory } from '../../../lib/customerLicenseFilter';
import { Translate as t } from '../../../config/Translate';
import {
    ORG_HQ,
    ORG_SUBSIDIARY,
    LICENSE_STATUS_ALL,
    LICENSE_STATUS_ACTIVE,
    LICENSE_STATUS_SUSPEND,
    LICENSE_STATUS_EXPIRED,
    LICENSE_TYPE_ALL,
    LICENSE_TYPE_INITIAL,
    LICENSE_TYPE_RENEWAL,
} from '../../../config/appConstants';
import LicenseDetailModal from './LicenseDetailModal';
import CustomerLicensesTable from '../../../components/license/CustomerLicensesTable';
import IssueHistoryTable from '../../../components/license/IssueHistoryTable';
import IssueHistoryFilters from '../../../components/license/IssueHistoryFilters';
import LicenseListTabs from '../../../components/license/LicenseListTabs';
import PeriodFilter, { computeRange, FILTER_ROW_CLASS } from '../../../components/license/PeriodFilter';

const ALL_SUB_ORGS = '';
// HQ 범위 선택 sentinel — companyId 와 겹치지 않는 값.
const SCOPE_ALL = '__all__';
const SCOPE_HQ_DIRECT = '__hqDirect__';
// "전체" 조회는 store 슬롯을 건드리지 않도록 어떤 reducer 도 처리하지 않는 action type 으로 dispatch 한다.
const LOCAL_ONLY_ACTION = 'LICENSE_ISSUANCE_TAB_LOCAL_ONLY';
const EMPTY_RESULT = { items: [], failedDealerIds: [] };

// 조회 응답을 { items: [], failedDealerIds: [] } 형태로 정규화 — 형태가 어긋나도 렌더가 죽지 않게.
const normalizeResult = (res) => ({
    items: Array.isArray(res?.items) ? res.items : [],
    failedDealerIds: Array.isArray(res?.failedDealerIds) ? res.failedDealerIds : [],
});

// FR-003/FR-004 — 발급 현황. 법인은 고객 라이선스 상세에서 정지·재개·메모·PC 비활성화 가능, HQ 는 조회 전용. 발급 액션 없음.
// 법인: 자기 제외 하위 전체. HQ: 전체(전사 고객 라이선스) / HQ 직속 / 법인 선택(법인 자신 + 하위).
const LicenseIssuanceTab = ({ user, role, companies, product: productProp }) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);

    // companies 비었으면 마운트 시 1회 로드 (LicenseDealerTab 과 동일 패턴).
    useEffect(() => {
        if (!companies || companies.length === 0) {
            dispatch(CompanyAction.GetCompany());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const companyMap = useMemo(() => {
        const map = {};
        (Array.isArray(companies) ? companies : []).forEach((c) => { if (c?.companyId) map[c.companyId] = c; });
        if (user?.companyId && user?.companyInfo && !map[user.companyId]) {
            map[user.companyId] = user.companyInfo;
        }
        return map;
    }, [companies, user]);

    const companyNameOf = useMemo(
        () => (id) => companyMap[id]?.name || id,
        [companyMap],
    );

    // HQ 범위 선택: 전체(기본) / HQ 직속 / 법인. 법인 목록은 이름순, 판정은 getLicenseRole 의 회사 판정과 동일.
    const [selectedScope, setSelectedScope] = useState(SCOPE_ALL);
    const subsidiaries = useMemo(
        () => (Array.isArray(companies) ? companies : [])
            .filter((c) => isLicenseCorpCompany(c))
            .sort((a, b) => String(a?.name ?? a?.companyId).localeCompare(String(b?.name ?? b?.companyId))),
        [companies],
    );

    const isHq = role === ORG_HQ;
    // 고객 라이선스 조작(정지·재개·메모·PC 좌석)은 법인만 — 개편 전 법인 관리 탭과 같다. HQ 는 조회 전용.
    const canOperate = role === ORG_SUBSIDIARY;

    // HQ 루트 id — 사용자 회사에서 parentCompanyId 를 따라 올라가 isRootCompany 인 회사. 손상(순환·미적재)이면 null.
    const hqRootId = useMemo(() => {
        if (!isHq || !user?.companyId) return null;
        const visited = new Set();
        let cur = companyMap[user.companyId];
        while (cur?.companyId && !visited.has(cur.companyId)) {
            if (isRootCompany(cur)) return cur.companyId;
            visited.add(cur.companyId);
            cur = companyMap[cur.parentCompanyId];
        }
        if (Array.isArray(companies) && companies.length > 0) {
            console.warn('[LicenseIssuanceTab] HQ root company not resolved', {
                handler: 'LicenseIssuanceTab.hqRootId',
                callerCompanyId: user.companyId,
                reason: 'root not found in companies (broken parent chain)',
            });
        }
        return null;
    }, [isHq, user?.companyId, companyMap, companies]);

    const isAllScope = isHq && selectedScope === SCOPE_ALL;
    const rootId = isHq
        ? (selectedScope === SCOPE_ALL || selectedScope === SCOPE_HQ_DIRECT ? null : selectedScope)
        : (role === ORG_SUBSIDIARY ? (user?.companyId ?? null) : null);
    // 범위가 바뀌면 안쪽 하위 조직 필터를 리셋하기 위한 키.
    const scopeKey = isHq ? selectedScope : (rootId ?? '');

    // 조회 대상 조직 id: 법인 로그인=하위 전체(자기 제외) / HQ 법인 선택=법인 자신 + 하위 / HQ 직속 / HQ 전체=[](별도 경로).
    const scopeIds = useMemo(() => {
        if (!isHq) return collectDescendantCompanyIds(companies, rootId);
        if (selectedScope === SCOPE_ALL) return [];
        if (selectedScope === SCOPE_HQ_DIRECT) {
            return collectHqDirectCompanyIds(companies, hqRootId, isLicenseCorpCompany);
        }
        if (!subsidiaries.some((c) => c.companyId === selectedScope)) {
            console.warn('[LicenseIssuanceTab] selected scope is not a corp company', {
                handler: 'LicenseIssuanceTab.scopeIds',
                callerCompanyId: user?.companyId ?? null,
                targetCompanyId: selectedScope,
                reason: 'not in corp list',
            });
            return [];
        }
        return [selectedScope, ...collectDescendantCompanyIds(companies, selectedScope)];
    }, [isHq, companies, rootId, selectedScope, hqRootId, subsidiaries, user?.companyId]);
    const idsKey = scopeIds.join('|');

    const [dealerFilter, setDealerFilter] = useState(ALL_SUB_ORGS);
    useEffect(() => { setDealerFilter(ALL_SUB_ORGS); }, [scopeKey]);

    // 기간/상태/검색 필터 — 키와 기본값은 DealerIssuePanel·구 LicenseSubsidiaryTab 과 동일.
    // 기간 필터는 탭별로 따로 둔다 — 발급 이력(issuedAt 조회 범위) / 고객 라이선스(발급일 created 거르기).
    const [historyPeriodFilter, setHistoryPeriodFilter] = useState({ preset: 'today', from: '', to: '' });
    const [licensesPeriodFilter, setLicensesPeriodFilter] = useState({ preset: 'today', from: '', to: '' });
    const [historyTypeFilter, setHistoryTypeFilter] = useState('all');
    const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
    const [licensesStatusFilter, setLicensesStatusFilter] = useState(LICENSE_STATUS_ALL);
    const [licensesTypeFilter, setLicensesTypeFilter] = useState(LICENSE_TYPE_ALL);
    const [licensesExpiresFilter, setLicensesExpiresFilter] = useState({ preset: 'all', from: '', to: '' });
    const [licensesSearchFilter, setLicensesSearchFilter] = useState('');
    const [detailModal, setDetailModal] = useState({ show: false, license: null });

    const historyPeriodRange = useMemo(() => computeRange(historyPeriodFilter), [historyPeriodFilter]);
    const licensesPeriodRange = useMemo(() => computeRange(licensesPeriodFilter), [licensesPeriodFilter]);
    const expiresRange = useMemo(() => computeRange(licensesExpiresFilter), [licensesExpiresFilter]);
    // 'expired' 는 BE 비지원 — 'all' 로 받아 화면에서 거른다(토글 시 중복 요청 방지).
    const apiStatus = licensesStatusFilter === LICENSE_STATUS_EXPIRED ? LICENSE_STATUS_ALL : licensesStatusFilter;

    // 조회 결과는 로컬 state (store 슬롯 미사용). 요청 순번 ref 로 늦게 온 이전 응답을 버린다.
    const [history, setHistory] = useState(EMPTY_RESULT);
    const [licenses, setLicenses] = useState(EMPTY_RESULT);
    const historySeq = useRef(0);
    const licensesSeq = useRef(0);

    useEffect(() => () => { historySeq.current += 1; licensesSeq.current += 1; }, []);

    useEffect(() => {
        historySeq.current += 1;
        const seq = historySeq.current;
        // 전체 범위는 전사 반복 조회가 무거워 발급 이력을 조회하지 않는다(안내만 표시).
        if (isAllScope || scopeIds.length === 0) {
            setHistory(EMPTY_RESULT);
            return;
        }
        Promise.resolve(dispatch(SmartDentAction.QueryIssuedQrsForDealers(scopeIds, {
            from: historyPeriodRange.fromTs,
            to: historyPeriodRange.toTs,
            product,
        }))).then((res) => {
            if (seq !== historySeq.current) return;
            setHistory(normalizeResult(res));
        }).catch((error) => {
            if (seq !== historySeq.current) return;
            console.warn('[LicenseIssuanceTab] issue history query failed', {
                handler: 'LicenseIssuanceTab.historyEffect',
                scopeKey,
                reason: error?.message ?? 'unknown error',
            });
            setHistory(EMPTY_RESULT);
        });
        // scopeIds 는 idsKey 로 대표 (내용 동일 시 재조회 방지).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, scopeKey, isAllScope, idsKey, historyPeriodRange.fromTs, historyPeriodRange.toTs, product]);

    // 고객 라이선스 조회 — 최초/범위 변경 시와 조작 성공 후 재조회가 같은 경로를 쓴다(요청 순번 가드 포함).
    const fetchLicenses = () => {
        licensesSeq.current += 1;
        const seq = licensesSeq.current;
        if (!isAllScope && scopeIds.length === 0) {
            setLicenses(EMPTY_RESULT);
            return;
        }
        // 전체(HQ): 개편 전 법인 관리 탭과 같은 한 번 호출 — dealerId 없이 전사 목록(BE 가 su 무필터).
        const request = isAllScope
            ? SmartDentAction.GetDealerCustomerLicenses(null, { status: apiStatus, product }, LOCAL_ONLY_ACTION)
            : SmartDentAction.QuerySubOrgCustomerLicenses(scopeIds, { status: apiStatus, product });
        Promise.resolve(dispatch(request)).then((res) => {
            if (seq !== licensesSeq.current) return;
            const normalized = normalizeResult(res);
            // 법인 경로: 메모 저장(UpdateLicenseMemo)의 가시성 검사가 corpCustomerLicenses 를 보므로 개편 전처럼 슬롯에도 둔다.
            if (canOperate) {
                dispatch({ type: SmartDentConstant.GET_CORP_CUSTOMER_LICENSES, customerLicenses: normalized.items, productKey: (product ?? DEFAULT_LICENSE_PRODUCT).key });
            }
            setLicenses(normalized);
        }).catch((error) => {
            if (seq !== licensesSeq.current) return;
            console.warn('[LicenseIssuanceTab] customer licenses query failed', {
                handler: 'LicenseIssuanceTab.licensesEffect',
                scopeKey,
                reason: error?.message ?? 'unknown error',
            });
            setLicenses(EMPTY_RESULT);
        });
    };

    useEffect(() => {
        fetchLicenses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, scopeKey, isAllScope, idsKey, apiStatus, product]);

    const failedNames = useMemo(() => {
        const ids = Array.from(new Set([...history.failedDealerIds, ...licenses.failedDealerIds]));
        return ids.map((id) => companyNameOf(id));
    }, [history.failedDealerIds, licenses.failedDealerIds, companyNameOf]);

    const filteredHistory = useMemo(() => {
        const scoped = dealerFilter === ALL_SUB_ORGS ? history.items : history.items.filter((qr) => qr?.dealerId === dealerFilter);
        return filterIssueHistory(scoped, { type: historyTypeFilter, status: historyStatusFilter });
    }, [history.items, dealerFilter, historyTypeFilter, historyStatusFilter]);

    const filteredLicenses = useMemo(() => {
        const scoped = dealerFilter === ALL_SUB_ORGS
            ? licenses.items
            : licenses.items.filter((lic) => resolveLicenseOwnerId(lic) === dealerFilter);
        return applyCustomerLicenseFilters(scoped, {
            type: licensesTypeFilter,
            status: licensesStatusFilter,
            periodRange: licensesPeriodRange,
            expiresRange,
            search: licensesSearchFilter,
        });
    }, [licenses.items, dealerFilter, licensesTypeFilter, licensesStatusFilter,
        licensesPeriodRange, expiresRange, licensesSearchFilter]);

    const statusOptions = useMemo(() => [
        { value: LICENSE_STATUS_ALL, label: `${t('common.label.status')}: ${t('common.label.all')}` },
        { value: LICENSE_STATUS_ACTIVE, label: t('Active') },
        { value: LICENSE_STATUS_SUSPEND, label: t('Suspended') },
        { value: LICENSE_STATUS_EXPIRED, label: t('Expired') },
    ], []);
    const typeOptions = useMemo(() => [
        { value: LICENSE_TYPE_ALL, label: `${t('common.field.type')}: ${t('common.label.all')}` },
        { value: LICENSE_TYPE_INITIAL, label: t('license.label.initial') },
        { value: LICENSE_TYPE_RENEWAL, label: t('license.label.renewal') },
    ], []);

    // 법인(HQ 범위) · 하위 조직 콤보 — 두 안쪽 탭의 필터 줄에서 기간 다음에 같은 조각을 쓴다(상태는 탭이 공유).
    // 라벨 없이 첫 옵션 접두·aria-label 로 구분한다. id 는 중복을 피하려 한 곳(primary)에만 붙인다.
    const scopeControls = (primary) => (
        <>
            {isHq && (
                <select
                    id={primary ? 'issuance-subsidiary-select' : undefined}
                    className="form-select form-select-sm w-auto"
                    value={selectedScope}
                    onChange={(e) => setSelectedScope(e.target.value)}
                    aria-label={t('license.label.selectSubsidiary')}
                >
                    <option value={SCOPE_ALL}>{t('license.label.selectSubsidiary')}: {t('common.label.all')}</option>
                    <option value={SCOPE_HQ_DIRECT}>{t('license.label.hqDirectOrgs')}</option>
                    {subsidiaries.map((c) => (
                        <option key={c.companyId} value={c.companyId}>{c?.name || c.companyId}</option>
                    ))}
                </select>
            )}
            {!isAllScope && (
                <select
                    className="form-select form-select-sm w-auto"
                    value={dealerFilter}
                    onChange={(e) => setDealerFilter(e.target.value)}
                    aria-label={t('license.label.allSubOrgs')}
                >
                    <option value={ALL_SUB_ORGS}>{t('license.label.allSubOrgs')}</option>
                    {scopeIds.map((id) => (
                        <option key={id} value={id}>{companyNameOf(id)}</option>
                    ))}
                </select>
            )}
        </>
    );

    // 조회 전용 안내 — 필터 줄 맨 끝 작은 글씨.
    const readOnlyNote = isHq ? (
        <>
            <span className="badge bg-secondary">{t('license.label.readOnly')}</span>
            <small className="text-muted">{t('license.message.subOrgsOnlyNotice')}</small>
        </>
    ) : null;

    return (
        <div>
            {failedNames.length > 0 && (
                <div className="alert alert-warning py-2 mb-2" role="alert">
                    {t('license.message.issuanceLoadPartialFailed')} {failedNames.join(', ')}
                </div>
            )}

            <LicenseListTabs
                tabs={[
                    {
                        key: 'issueHistory',
                        label: t('license.label.issueHistory'),
                        content: isAllScope ? (
                            <>
                                {/* 전체 범위는 발급 이력을 조회하지 않지만, 법인 콤보는 남겨 범위를 바꿀 수 있게 한다. */}
                                <div className={FILTER_ROW_CLASS}>
                                    {scopeControls(true)}
                                    <div className="ms-auto d-flex align-items-center gap-2">{readOnlyNote}</div>
                                </div>
                                <div className="alert alert-info py-2 mb-2">{t('license.message.selectSubsidiaryFirst')}</div>
                            </>
                        ) : (
                            <>
                                <IssueHistoryFilters
                                    leading={(
                                        <>
                                            <PeriodFilter value={historyPeriodFilter} onChange={setHistoryPeriodFilter} />
                                            {scopeControls(true)}
                                        </>
                                    )}
                                    note={readOnlyNote}
                                    typeFilter={historyTypeFilter}
                                    onTypeChange={setHistoryTypeFilter}
                                    statusFilter={historyStatusFilter}
                                    onStatusChange={setHistoryStatusFilter}
                                />
                                <IssueHistoryTable
                                    items={filteredHistory}
                                    canOperate={false}
                                    showCompany
                                    companyNameOf={companyNameOf}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'customerLicenses',
                        label: t('license.label.customerLicenses'),
                        content: (
                            <CustomerLicensesTable
                                licenses={filteredLicenses}
                                companyMap={companyMap}
                                extraFilters={scopeControls(false)}
                                note={readOnlyNote}
                                canEdit={canOperate}
                                showDetail
                                onDetail={(lic) => setDetailModal({ show: true, license: lic })}
                                periodFilter={licensesPeriodFilter}
                                onPeriodChange={setLicensesPeriodFilter}
                                statusFilter={licensesStatusFilter}
                                statusOptions={statusOptions}
                                onStatusChange={setLicensesStatusFilter}
                                typeFilter={licensesTypeFilter}
                                typeOptions={typeOptions}
                                onTypeChange={setLicensesTypeFilter}
                                expiresFilter={licensesExpiresFilter}
                                onExpiresChange={setLicensesExpiresFilter}
                                searchFilter={licensesSearchFilter}
                                onSearchChange={setLicensesSearchFilter}
                            />
                        ),
                    },
                ]}
            />

            <LicenseDetailModal
                show={detailModal.show}
                license={detailModal.license}
                role={role}
                canOperate={canOperate}
                companyMap={companyMap}
                onClose={() => setDetailModal({ ...detailModal, show: false })}
                onSuccess={() => {
                    setDetailModal({ ...detailModal, show: false });
                    fetchLicenses();
                }}
            />
        </div>
    );
};

const mapState = (state) => ({
    companies: state.CompanyReducer.companies,
});

export default connect(mapState)(LicenseIssuanceTab);
